// Helper function to hash data with SHA-256 using Web Crypto API
const hashData = async (data: string): Promise<string> => {
  const encoder = new TextEncoder();
  const dataBuffer = encoder.encode(data);
  const hashBuffer = await crypto.subtle.digest('SHA-256', dataBuffer);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
};

interface TikTokContent {
  content_id: string;
  content_type: 'product' | 'product_group';
  content_name: string;
}

interface TikTokEventParams {
  contents: TikTokContent[];
  value?: number;
  currency?: string;
  search_string?: string;
}

interface TikTokIdentifyParams {
  email?: string;
  phone_number?: string;
  external_id?: string;
}

// Debug function to check TikTok Pixel initialization
const checkTikTokPixel = () => {
  if (typeof window === 'undefined') {
    console.warn('TikTok Pixel: Window object not available');
    return false;
  }
  
  if (!(window as any).ttq) {
    console.warn('TikTok Pixel: ttq object not found. Make sure TikTok Pixel is properly initialized.');
    return false;
  }
  
  return true;
};

// Function to identify user
export const identifyTikTokUser = async (params: TikTokIdentifyParams) => {
  console.log('TikTok Pixel: Attempting to identify user with params:', params);
  
  if (!checkTikTokPixel()) return;

  try {
    const hashedParams: TikTokIdentifyParams = {};
    
    if (params.email) {
      hashedParams.email = await hashData(params.email);
      console.log('TikTok Pixel: Hashed email:', hashedParams.email);
    }
    if (params.phone_number) {
      hashedParams.phone_number = await hashData(params.phone_number);
      console.log('TikTok Pixel: Hashed phone number:', hashedParams.phone_number);
    }
    if (params.external_id) {
      hashedParams.external_id = await hashData(params.external_id);
      console.log('TikTok Pixel: Hashed external ID:', hashedParams.external_id);
    }

    console.log('TikTok Pixel: Calling identify with hashed params:', hashedParams);
    (window as any).ttq.identify(hashedParams);
    console.log('TikTok Pixel: Identify call completed');
  } catch (error) {
    console.error('TikTok Pixel: Error in identifyTikTokUser:', error);
  }
};

// Function to track TikTok events
export const trackTikTokEvent = (eventName: string, params: TikTokEventParams) => {
  console.log(`TikTok Pixel: Attempting to track event "${eventName}" with params:`, params);
  
  if (!checkTikTokPixel()) return;

  try {
    (window as any).ttq.track(eventName, params);
    console.log(`TikTok Pixel: Event "${eventName}" tracked successfully`);
  } catch (error) {
    console.error(`TikTok Pixel: Error tracking event "${eventName}":`, error);
  }
};

// Function to track collab application view
export const trackCollabApplicationView = async (postId: string, postContent: string, currentUser: any) => {
  console.log('TikTok Pixel: Starting collab application view tracking', {
    postId,
    postContent,
    currentUser: currentUser ? 'present' : 'missing'
  });

  if (!currentUser) {
    console.warn('TikTok Pixel: No current user available for tracking');
    return;
  }

  if (!checkTikTokPixel()) return;

  try {
    // Identify user with hashed PII data
    await identifyTikTokUser({
      email: currentUser.email,
      external_id: currentUser.id
    });

    const contentParams = {
      content_id: postId,
      content_type: 'product' as const,
      content_name: postContent || 'Collaboration Application'
    };

    // Track ViewContent event
    trackTikTokEvent('ViewContent', {
      contents: [contentParams],
      value: 0,
      currency: 'USD'
    });

    // Track AddToWishlist event (since user is interested in the collab)
    trackTikTokEvent('AddToWishlist', {
      contents: [contentParams],
      value: 0,
      currency: 'USD'
    });

    // Track InitiateCheckout event (since user is starting the application process)
    trackTikTokEvent('InitiateCheckout', {
      contents: [contentParams],
      value: 0,
      currency: 'USD'
    });

    console.log('TikTok Pixel: All collab application view events tracked successfully');
  } catch (error) {
    console.error('TikTok Pixel: Error in trackCollabApplicationView:', error);
  }
};

// Specific event tracking functions
export const trackViewContent = (params: TikTokEventParams) => {
  trackTikTokEvent('ViewContent', params);
};

export const trackAddToWishlist = (params: TikTokEventParams) => {
  trackTikTokEvent('AddToWishlist', params);
};

export const trackSearch = (params: TikTokEventParams & { search_string: string }) => {
  trackTikTokEvent('Search', params);
};

export const trackAddPaymentInfo = (params: TikTokEventParams) => {
  trackTikTokEvent('AddPaymentInfo', params);
};

export const trackAddToCart = (params: TikTokEventParams) => {
  trackTikTokEvent('AddToCart', params);
};

export const trackInitiateCheckout = (params: TikTokEventParams) => {
  trackTikTokEvent('InitiateCheckout', params);
};

export const trackPlaceAnOrder = (params: TikTokEventParams) => {
  trackTikTokEvent('PlaceAnOrder', params);
};

export const trackCompleteRegistration = (params: TikTokEventParams) => {
  trackTikTokEvent('CompleteRegistration', params);
};

export const trackCompletePayment = (params: TikTokEventParams) => {
  trackTikTokEvent('CompletePayment', params);
}; 